import { fromPairs } from 'lodash';
import { Content, Empty, Loaded, Loading } from 'stores/core';
import {
  AccountPermissionSet,
  AccountPermissionSetType,
  PermissionSet,
  ProjectPermissionSet,
  ProjectPermissionSetType,
} from './permission-set';

export type PermissionSetLookup = Record<
  ProjectPermissionSetType,
  Content<ProjectPermissionSet>
> &
  Record<AccountPermissionSetType, Content<AccountPermissionSet>>;
export type PermissionSetKey = keyof PermissionSetLookup;

export interface PermissionSetsStore {
  byKey: PermissionSetLookup;
}

const KEYS: PermissionSetKey[] = Object.values(ProjectPermissionSetType)
  .filter((x) => typeof x === 'number')
  .map((x) => x as PermissionSetKey)
  .concat(
    ...Object.values(AccountPermissionSetType)
      .filter((x) => typeof x === 'number')
      .map((x) => x as PermissionSetKey),
  );

const EMPTY: PermissionSetsStore = {
  byKey: fromPairs(
    KEYS.map((x) => [x, Empty()]),
  ) as unknown as PermissionSetLookup,
};

const load =
  (key: PermissionSetKey) =>
  ({ byKey }: PermissionSetsStore): PermissionSetsStore => ({
    byKey: { ...byKey, [key]: Loading() },
  });

const loadAll = (keys: PermissionSetKey[]) => (store: PermissionSetsStore) =>
  keys.reduce((prev, key) => load(key)(prev), store);

const put =
  (set: PermissionSet) =>
  ({ byKey }: PermissionSetsStore): PermissionSetsStore => ({
    byKey: { ...byKey, [set.permissionSetType]: Loaded(set) },
  });

const putAll = (sets: PermissionSet[]) => (store: PermissionSetsStore) =>
  sets.reduce((prev, set) => put(set)(prev), store);

export const PermissionSetsStore = {
  empty: () => EMPTY,
  keys: () => KEYS,
  load,
  loadAll,
  put,
  putAll,
};
